import { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, CheckCircle2, Check } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useLocation } from "@/components/location-provider";
import { PageHeader } from "@/components/page-header";

const RULES: Record<string, string[]> = {
  Thunderstorm: ["Stay indoors away from windows", "Unplug sensitive electronics", "Avoid open water and tall trees", "Have flashlight and batteries ready"],
  Heat: ["Stay hydrated — drink water every 30 min", "Avoid outdoor activity 11am–4pm", "Check on elderly neighbors", "Never leave children or pets in vehicles"],
  Flood: ["Move to higher ground if advised", "Avoid walking through floodwater", "Prepare emergency go-bag", "Monitor local emergency channels"],
  Wind: ["Secure outdoor furniture and loose items", "Stay away from windows during gusts", "Have backup power plan ready", "Avoid driving high-profile vehicles"],
  Snow: ["Stock up on essentials before storm", "Keep pipes from freezing — drip faucets", "Clear walkways to prevent ice buildup", "Keep emergency blankets accessible"],
  "Air Quality": ["Limit outdoor exertion", "Keep windows closed", "Run air purifier if available", "Wear N95 mask if going outside"],
};

function getChecklist(alerts: { event: string }[], aqiLevel: number): { category: string; items: string[] }[] {
  const result: { category: string; items: string[] }[] = [];
  for (const alert of alerts) {
    for (const [key, items] of Object.entries(RULES)) {
      if (alert.event.toLowerCase().includes(key.toLowerCase())) {
        result.push({ category: alert.event, items });
      }
    }
  }
  if (aqiLevel >= 4 && !result.some((r) => r.category.includes("Air"))) {
    result.push({ category: "Poor Air Quality", items: RULES["Air Quality"] });
  }
  return result;
}

export default function PreparednessPage() {
  const [checklist, setChecklist] = useState<{ category: string; items: string[] }[]>([]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const { current: location } = useLocation();

  useEffect(() => {
    setLoading(true);
    fetch(`/api/weather?lat=${location.lat}&lon=${location.lon}`)
      .then((r) => r.json())
      .then((d) => {
        const alerts = d.weather?.alerts || [];
        const aqiLevel = d.aqi?.list?.[0]?.main?.aqi || 1;
        setChecklist(getChecklist(alerts, aqiLevel));
      })
      .finally(() => setLoading(false));
  }, [location.lat, location.lon]);

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (loading) return <div className="space-y-4 animate-slide-up"><Skeleton className="h-52 rounded-2xl" /><Skeleton className="h-52 rounded-2xl" /></div>;

  const totalItems = checklist.reduce((sum, g) => sum + g.items.length, 0);
  const checkedItems = Array.from(checked).filter((c) =>
    checklist.some((g) => c.startsWith(g.category))
  ).length;

  return (
    <div className="space-y-5 animate-slide-up">
      <PageHeader
        eyebrow="Action Plan"
        title="Preparedness"
        subtitle={`Checklist for ${location.name}`}
        action={
          totalItems > 0 && (
            <GlassPanel intensity="low" className="inline-flex items-center gap-2 px-3 py-1.5 text-xs !rounded-full">
              <Check className="h-3 w-3 text-primary" />
              <span className="font-semibold">{checkedItems}/{totalItems}</span>
            </GlassPanel>
          )
        }
      />

      {checklist.length === 0 ? (
        <GlassPanel intensity="low" className="p-12 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10 mx-auto mb-4">
            <ShieldCheck className="h-8 w-8 text-emerald-400" />
          </div>
          <h2 className="text-lg font-bold">All Clear</h2>
          <p className="text-sm text-muted-foreground mt-1">No active alerts require preparation.</p>
        </GlassPanel>
      ) : (
        <div className={`grid gap-4 ${checklist.length > 1 ? "lg:grid-cols-2" : ""}`}>
          {checklist.map((group) => {
            const done = group.items.filter((item) => checked.has(`${group.category}_${item}`)).length;
            const pct = group.items.length > 0 ? Math.round((done / group.items.length) * 100) : 0;
            return (
              <GlassPanel key={group.category} intensity="medium">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-border/20">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-destructive/10">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm">{group.category}</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="flex-1 h-1 rounded-full bg-muted/50 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-[10px] font-mono text-muted-foreground">{done}/{group.items.length}</span>
                    </div>
                  </div>
                </div>
                <div className="p-2 space-y-0.5">
                  {group.items.map((item) => {
                    const id = `${group.category}_${item}`;
                    const done = checked.has(id);
                    return (
                      <button
                        key={id}
                        onClick={() => toggle(id)}
                        className={cn(
                          "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-all duration-200",
                          done
                            ? "bg-primary/5 text-muted-foreground line-through"
                            : "hover:bg-muted/40 text-foreground"
                        )}
                      >
                        <CheckCircle2 className={cn(
                          "h-4 w-4 flex-shrink-0 transition-colors",
                          done ? "text-primary" : "text-muted-foreground/30"
                        )} />
                        <span>{item}</span>
                      </button>
                    );
                  })}
                </div>
              </GlassPanel>
            );
          })}
        </div>
      )}
    </div>
  );
}